import React from 'react'
import { notification } from 'antd'
import { AxiosError } from 'axios'
import { action, computed, makeAutoObservable, observable } from 'mobx'
import { computedFn } from 'mobx-utils'

import { parseErrorsToString } from 'Shared/utils'

import {
	AppDateFormats,
	AppDateWithTimeFormats,
	DEFAULT_APP_DATE_FORMAT,
	DEFAULT_APP_DATE_WITH_TIME_FORMAT,
	DEFAULT_APP_TIME_FORMAT
} from 'Core/constants'
import { TIMEOUT_ERROR_CODE } from 'Core/constants'
import {
	AxiosErrorResponse,
	defaultInfiniteLoaderData,
	DrawerProps,
	InfiniteLoaderData,
	LoadingBtnNames,
	ModalProps,
	NotificationProps,
	SidePreviewProps
} from 'Core/interfaces'
import HistoryStore from 'Core/stores/historyStore/HistoryStore'

import styles from 'Shared/atoms/Notifications/styles.module.scss'

export default class UIStore<RootStore extends HistoryStore> {
	@observable
	modalProps: ModalProps | null = null
	@observable
	sidePreviewProps: SidePreviewProps | null = null

	@observable
	drawerProps: DrawerProps | null = null

	@observable
	isModalOpened = false
	@observable
	isSidePreviewOpened = false

	@observable
	isDrawerOpened = false

	@observable
	isSidebarOpened = false

	@observable
	isRequestSubmitting = false

	@observable
	btnLoadingName: LoadingBtnNames | null = null

	@observable
	dateFormat: AppDateFormats | null = null

	@observable
	isDateFormatLoading: boolean = false

	@observable
	infiniteLoaderData: InfiniteLoaderData = defaultInfiniteLoaderData

	constructor(public rootStore: RootStore) {
		makeAutoObservable(this, {}, { autoBind: true })
	}

	@action
	showNotification = ({ type = 'info', msg }: NotificationProps) => {
		notification[type]({
			message: msg,
			className: styles.notification,
			placement: 'top'
		})
	}

	showSuccessNotification = (message: string) =>
		this.showNotification({
			type: 'success',
			msg: message
		})

	showErrorNotification = (error?: AxiosError, msg?: string) => {
		const data = error?.response?.data as AxiosErrorResponse
		const parsedMsg = parseErrorsToString(data?.errors, msg)
		this.showNotification({
			type: 'error',
			msg: error?.code === TIMEOUT_ERROR_CODE ? error.message : parsedMsg
		})
	}

	@action
	openModal = (payload: ModalProps) => {
		this.isModalOpened = true
		this.modalProps = payload
	}

	@action
	updateModalTitle = (title?: string) => {
		if (this.modalProps) {
			this.modalProps = { ...this.modalProps, title }
		}
	}

	@action
	updateModalWidth = (width: number) => {
		if (this.modalProps) {
			this.modalProps = { ...this.modalProps, width }
		}
	}

	@action
	updateModalClosable = (closable: boolean) => {
		if (this.modalProps) {
			this.modalProps = { ...this.modalProps, closable }
		}
	}

	@action
	openSidePreview = (payload: SidePreviewProps) => {
		this.isSidePreviewOpened = true
		this.sidePreviewProps = payload
	}

	@action
	closeModal = () => {
		if (this.modalProps && this.modalProps.onClose) {
			this.modalProps.onClose()
		}

		this.isModalOpened = false
		this.modalProps = null
	}

	@action
	openDrawer = (payload: DrawerProps) => {
		this.isDrawerOpened = true
		this.drawerProps = payload
	}

	@action
	refreshDrawerProps(payload: DrawerProps) {
		this.drawerProps = payload
	}

	@action
	setIsDrawerOpened = (value: boolean) => {
		this.isDrawerOpened = value
	}

	@action
	closeDrawer = async (event?: React.MouseEvent) => {
		if (event) {
			this.drawerProps?.onClose?.(event)
		}
		this.isDrawerOpened = false
	}

	@action
	resetDrawerProps() {
		if (!this.isDrawerOpened) {
			this.drawerProps = null
		}
	}

	@action
	closeModalWithoutProps = () => {
		this.isModalOpened = false
		this.modalProps = null
	}

	@action
	closeSidePreview = () => {
		if (this.sidePreviewProps && this.sidePreviewProps.onClose) {
			this.sidePreviewProps.onClose()
		}

		this.isSidePreviewOpened = false
		this.sidePreviewProps = null
	}

	@action
	openSidebar = () => {
		this.isSidebarOpened = true
	}

	@action
	closeSidebar = () => {
		this.isSidebarOpened = false
	}

	@action
	setIsRequestSubmitting = (status?: boolean) => {
		this.isRequestSubmitting = status ?? true
	}

	@action
	setIsBtnLoading = (btnName: LoadingBtnNames | null, status?: boolean) => {
		this.setIsRequestSubmitting(status)
		this.btnLoadingName = btnName
	}

	getIsBtnLoading = computedFn((btnName: LoadingBtnNames) => this.btnLoadingName === btnName)

	getIsBtnDisabled = computedFn(
		(btnName: LoadingBtnNames) => this.isRequestSubmitting && this.btnLoadingName !== btnName
	)

	getBtnConfig = (btnName: LoadingBtnNames, isDisabled?: boolean, isLoading?: boolean) => ({
		loading: this.getIsBtnLoading(btnName) || isLoading,
		disabled: this.getIsBtnDisabled(btnName) || isDisabled
	})

	@action
	setIsDateFormatLoading(isLoading: boolean) {
		this.isDateFormatLoading = isLoading
	}

	@action
	setCurrentDateFormat = (dateFormat: AppDateFormats) => {
		if (!dateFormat) {
			return
		}

		this.dateFormat = dateFormat.toUpperCase() as AppDateFormats
	}

	@computed
	get getCurrentDateFormat(): AppDateFormats {
		if (!this.dateFormat) {
			return DEFAULT_APP_DATE_FORMAT
		}

		return this.dateFormat
	}

	@computed
	get getCurrentDateWithTimeFormat(): AppDateWithTimeFormats {
		if (!this.dateFormat) {
			return DEFAULT_APP_DATE_WITH_TIME_FORMAT
		}

		return `${this.dateFormat} ${DEFAULT_APP_TIME_FORMAT}`
	}

	@computed
	get getPlaceholderDateFormat(): string {
		return this.getCurrentDateFormat.toLowerCase()
	}

	backToPreviousPage = () => {
		const prevPagePath = this.rootStore.getPreviousPath()
		this.rootStore.history.push(prevPagePath)
	}

	@action
	setInfiniteLoaderData({
		dataLength,
		pageSize,
		totalCount
	}: {
		dataLength: number
		pageSize: number
		totalCount: number
	}) {
		const correctTo = dataLength + pageSize > totalCount ? totalCount : dataLength + pageSize
		this.infiniteLoaderData = { from: dataLength + 1, to: correctTo }
	}
}
